import { jwtDecode } from 'jwt-decode';

interface KioskSession {
  token: string;
  studioId: string;
  expiresAt: number;
  createdAt: number;
}

interface KioskJWTPayload {
  studioId: string;
  exp: number;
  iat: number;
  type: 'kiosk';
}

const KIOSK_SESSION_KEY = 'kioskSession';
const SESSION_DURATION = 12 * 60 * 60 * 1000; // 12 hours in milliseconds

/**
 * Securely checks if the current session is in kiosk mode
 * Validates token expiration and authenticity
 */
export const isKioskMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  try {
    const sessionData = localStorage.getItem(KIOSK_SESSION_KEY);
    if (!sessionData) return false;

    const session: KioskSession = JSON.parse(sessionData);
    
    // Check if session has expired
    if (Date.now() > session.expiresAt) {
      clearKioskSession();
      return false;
    }

    // Validate JWT token structure
    if (!session.token || !isValidKioskToken(session.token)) {
      clearKioskSession();
      return false;
    }

    return true;
  } catch (error) {
    clearKioskSession();
    return false;
  }
};

/**
 * Validates the structure and expiration of a kiosk JWT token
 */
const isValidKioskToken = (token: string): boolean => {
  try {
    const decoded = jwtDecode<KioskJWTPayload>(token);
    
    // Check token type
    if (decoded.type !== 'kiosk') return false;
    
    // Check expiration (JWT exp is in seconds, Date.now() is in milliseconds)
    if (decoded.exp * 1000 < Date.now()) return false;
    
    // Check if studio ID exists
    if (!decoded.studioId) return false;
    
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Creates a secure kiosk session with server validation
 */
export const createKioskSession = async (
  studioId: string,
  authToken: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioId}/create-kiosk-session`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return { success: false, error: errorData.error || 'Failed to create kiosk session' };
    }

    const { kioskToken } = await response.json();
    
    // Store the secure session
    const session: KioskSession = {
      token: kioskToken,
      studioId,
      expiresAt: Date.now() + SESSION_DURATION,
      createdAt: Date.now(),
    };

    localStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify(session));
    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Network error' 
    };
  }
};

/**
 * Validates kiosk session with server
 */
export const validateKioskSession = async (): Promise<{ valid: boolean; studioId?: string }> => {
  if (!isKioskMode()) {
    return { valid: false };
  }

  try {
    const sessionData = localStorage.getItem(KIOSK_SESSION_KEY);
    if (!sessionData) return { valid: false };

    const session: KioskSession = JSON.parse(sessionData);
    
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/validate-kiosk-session`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ kioskToken: session.token }),
      }
    );

    if (!response.ok) {
      clearKioskSession();
      return { valid: false };
    }

    const { valid, studioId } = await response.json();
    
    if (!valid) {
      clearKioskSession();
      return { valid: false };
    }

    return { valid: true, studioId };
  } catch (error) {
    clearKioskSession();
    return { valid: false };
  }
};

/**
 * Gets the current kiosk studio ID if session is valid
 */
export const getKioskStudioId = (): string | null => {
  if (!isKioskMode()) return null;
  
  try {
    const sessionData = localStorage.getItem(KIOSK_SESSION_KEY);
    if (!sessionData) return null;

    const session: KioskSession = JSON.parse(sessionData);
    return session.studioId;
  } catch (error) {
    return null;
  }
};

/**
 * Clears the kiosk session
 */
export const clearKioskSession = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(KIOSK_SESSION_KEY);
  }
};

/**
 * Verifies kiosk password and ends session
 */
export const exitKioskMode = async (
  password: string,
  authToken: string
): Promise<{ success: boolean; error?: string }> => {
  const studioId = getKioskStudioId();
  if (!studioId) {
    return { success: false, error: 'No active kiosk session' };
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioId}/verify-kiosk-password`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ password }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return { success: false, error: errorData.error || 'Failed to verify password' };
    }

    const { isMatch } = await response.json();
    
    if (!isMatch) {
      return { success: false, error: 'Incorrect kiosk password' };
    }

    clearKioskSession();
    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Network error' 
    };
  }
}; 